import { computed, defineComponent, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useSmartBack } from '@/composables/use-smart-back'
import {
  ActionSheet as VanActionSheet,
  Icon as VanIcon,
  NavBar as VanNavBar,
  Search as VanSearch,
  Tab,
  Tabs,
  showFailToast,
  showSuccessToast,
  showToast,
} from 'vant'
import type { ComponentPublicInstance } from 'vue'
import { useAuthStore } from '@/stores/auth-store'
import { useUserStore } from '@/stores/user-store'
import { useInfiniteScroll } from '@/composables/use-infinite-scroll'
import {
  getApiV1ArticleTypeUser,
  getApiV1BlockList,
  getApiV1CommunityList,
  getApiV1UserByIdProfile,
  postApiV1BlockAdd,
  postApiV1BlockRemove,
} from '@/api/sdk.gen'
import type { Article, CommunityPost, UserPublicProfile } from '@/api/types.gen'
import emptyImg from '@/assets/empty-data.png'
import UserAvatar from '@/components/user-avatar'
import SharePopup from '@/components/share-popup'

function formatTime(ts?: number | string): string {
  if (!ts) return ''
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return String(ts)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

// 从标题中提取期号（匹配"264期："/"第264期"/"264-1"，没有就空字符串）
/** 列表项的彩种标识：区旗 + 文字，港彩 1、澳彩 2，其他不显示 */
function renderLotteryFlag(lotteryType?: number) {
  if (lotteryType === 1) {
    return (
      <div>
        <img src="/assets/49208/img/lottery-type-list-hk.png" alt="" />
        <span class="num">香港</span>
      </div>
    )
  }
  if (lotteryType === 2) {
    return (
      <div>
        <img src="/assets/49208/img/lottery-type-list-am.png" alt="" />
        <span class="num">澳门</span>
      </div>
    )
  }
  return null
}

/** 内容 tab 对应 community/list 的 category：12→2、13→1，其余不传 */
function communityCategoryOf(type: number): 1 | 2 | undefined {
  if (type === 12) return 2
  if (type === 13) return 1
  return undefined
}

function extractPeriod(title?: string): string {
  if (!title) return ''
  const m = /(?:第)?(\d{2,4})\s*期/.exec(title)
  return m?.[1] ?? ''
}

// 内容 tab：六合论坛/图解小组/竞猜小组 走 community/list（target_id），资料大全走 articleType/user
const CONTENT_TABS = [
  { key: 3, label: '六合论坛' },
  { key: 4, label: '资料大全' },
  { key: 12, label: '图解小组' },
  { key: 13, label: '竞猜小组' },
]

// CommunityPost → Article 适配，复用既有 ForumListItem 渲染
function communityToArticle(p: CommunityPost): Article {
  const ts = p.publish_time
    ? new Date(p.publish_time * 1000).toISOString()
    : p.created_at || ''
  return {
    id: p.id,
    title: p.title,
    avatar: p.author?.avatar,
    userName: p.author?.nickname,
    userId: p.user_id,
    createdAt: ts,
    support: p.thumb_up_count ?? p.likes ?? 0,
    visit: p.click_count ?? 0,
    comments_count: p.comments ?? 0,
    lotteryType: p.lottery_type,
    content: p.content,
  }
}

const REGION_TABS = [
  { key: 0, label: '全部' },
  { key: 2, label: '澳彩' },
  { key: 1, label: '港彩' },
]

// 按 49208 user-home 的 .forum-list > li 结构一比一复刻
// withLabel=true 对齐高手论坛（label + van-col--16）
// withLabel=false 对齐资料大全（无 label + van-col--19）
const ForumListItem = defineComponent({
  props: {
    article: { type: Object as () => Article, required: true },
    withLabel: { type: Boolean, default: true },
    to: { type: String, required: true },
  },
  setup(props) {
    return () => {
      const a = props.article
      const period = extractPeriod(a.title)
      return (
        // 样式挂在 `.forum-list li` 上：li 保留，链接放里面包住整条内容
        <li>
          <RouterLink class="btn-reset block" to={props.to}>
          <div class="heads van-row">
            <div class="van-col van-col--20">
              <div class="user-img">
                <div class="left-image">
                  <div class="van-image van-image--round">
                    <UserAvatar src={a.avatar} />
                  </div>
                </div>
                <div class="user-info">
                  <div class="name">
                    {a.userName}
                    <span class="reportInfo"></span>
                  </div>
                  <div class="time">{formatTime(a.createdAt)}</div>
                </div>
              </div>
            </div>
            <div class="van-col van-col--2"></div>
          </div>
          <div class="title van-row">
            {props.withLabel && period ? (
              <div class="van-col" style="margin-right:10px">
                <label>{period}</label>
              </div>
            ) : null}
            <div class={['van-col', props.withLabel ? 'van-col--16' : 'van-col--19']}>
              <div class="txt van-ellipsis">{a.title}</div>
            </div>
            <div class="van-col van-col--5"></div>
          </div>
          <div class="text van-multi-ellipsis--l3"></div>
          <div class="tool-ft tom-col van-row">
            <div class="van-col van-col--6">
              <VanIcon name="good-job-o" size="15" />
              <span class="num">{a.support ?? 0}</span>
            </div>
            <div class="van-col van-col--6">
              <VanIcon name="eye-o" size="16" />
              <span class="num">{a.visit ?? 0}</span>
            </div>
            <div class="van-col van-col--6">
              <VanIcon name="chat-o" size="15" />
              <span class="num">{a.comments_count ?? 0}</span>
            </div>
            <div class="ntool van-col van-col--6">
              {/* 49208 chunk-user-home 列表项 .ntool 1:1：灰底 + 区旗图标 + 灰文字 */}
              {renderLotteryFlag(a.lotteryType)}
            </div>
          </div>
          </RouterLink>
        </li>
      )
    }
  },
})

export default defineComponent({
  name: 'UserProfilePage',
  setup() {
    const route = useRoute()
    const router = useRouter()
  const goBack = useSmartBack()
    const authStore = useAuthStore()
    const userStore = useUserStore()

    const userId = computed(() => Number(route.params.id))
    const isSelf = computed(() => userId.value === authStore.user?.id)
    const profile = ref<UserPublicProfile | null>(null)
    const isFollowed = computed(() => profile.value?.isFollowing ?? false)
    const type = ref(3)
    const region = ref(0)
    const keyword = ref('')
    let debounceTimer: ReturnType<typeof setTimeout> | null = null

    // 右上角 ActionSheet 菜单
    const menuShow = ref(false)
    const isBlocked = ref(false)
    const blockBusy = ref(false)
    const shareRef = ref<ComponentPublicInstance<unknown, { init: () => void }> | null>(null)

    async function fetchBlockStatus() {
      if (!authStore.isLoggedIn || isSelf.value) {
        isBlocked.value = false
        return
      }
      try {
        const res = await getApiV1BlockList({ query: { page: 1, size: 200 } })
        const data = res.data as { code: number; data: { list: { user_id?: number }[] } }
        if (data.code === 0) {
          isBlocked.value = (data.data?.list ?? []).some((u) => u.user_id === userId.value)
        }
      } catch {
        // ignore
      }
    }

    function handleShare() {
      shareRef.value?.init()
    }

    async function handleToggleBlock() {
      if (!authStore.isLoggedIn) {
        router.push('/login')
        return
      }
      if (blockBusy.value) return
      blockBusy.value = true
      const wasBlocked = isBlocked.value
      try {
        if (wasBlocked) {
          await postApiV1BlockRemove({ body: { block_user_id: userId.value } })
          isBlocked.value = false
          showSuccessToast('已取消拉黑')
        } else {
          await postApiV1BlockAdd({ body: { block_user_id: userId.value } })
          isBlocked.value = true
          showSuccessToast('拉黑成功')
          if (profile.value?.isFollowing) {
            profile.value = { ...profile.value, isFollowing: false }
          }
        }
      } catch {
        showFailToast('操作失败')
      } finally {
        blockBusy.value = false
      }
    }

    function onMenuSelect(action: { name: string }) {
      menuShow.value = false
      if (action.name === '分享') {
        // 等 ActionSheet 关闭动画结束再展开分享面板，避免两个 popup 的 transition 互相打断
        setTimeout(handleShare, 320)
      } else {
        handleToggleBlock()
      }
    }

    const menuActions = computed(() => [
      { name: '分享' },
      {
        name: isBlocked.value ? '取消拉黑' : '拉黑',
        color: '#ff5e4f',
      },
    ])

    const regionLabel = computed(() => {
      const tab = REGION_TABS.find((t) => t.key === region.value)
      return tab?.label || ''
    })
    const cplaceholder = computed(() =>
      region.value === 0 ? '查找全部发布的帖子' : `查找${regionLabel.value}下发布的帖子`,
    )

    async function fetchProfile() {
      if (!userId.value) return
      try {
        const res = await getApiV1UserByIdProfile({ path: { id: userId.value } })
        const data = res.data as { code: number; data: UserPublicProfile | null }
        if (data.code === 0 && data.data) profile.value = data.data
      } catch {
        // ignore
      }
    }

    const { items, isEmpty, load, reset } = useInfiniteScroll<Article>(async (page, size) => {
      if (!userId.value) return { list: [], total: 0 }

      // 资料大全 走原 article 接口
      if (type.value === 4) {
        const res = await getApiV1ArticleTypeUser({
          query: {
            page,
            size,
            userId: userId.value,
            lotteryType: region.value === 0 ? undefined : (region.value as 1 | 2),
          },
        })
        const data = res.data as {
          code: number
          data: { list: Article[]; total: number }
        }
        return data.code === 0 ? data.data : { list: [], total: 0 }
      }

      // 六合论坛 / 图解小组 / 竞猜小组 走 community/list
      const category = communityCategoryOf(type.value)
      const res = await getApiV1CommunityList({
        query: {
          page,
          size,
          target_id: userId.value,
          category,
          lottery_type: region.value === 0 ? undefined : region.value,
          keyword: keyword.value || undefined,
        },
      })
      const data = res.data as {
        code: number
        data: { list?: CommunityPost[]; total?: number } | null
      }
      if (data.code !== 0 || !data.data) return { list: [], total: 0 }
      return {
        list: (data.data.list || []).map(communityToArticle),
        total: data.data.total ?? 0,
      }
    })

    onMounted(() => {
      fetchProfile()
      fetchBlockStatus()
      load()
    })

    watch(userId, () => {
      fetchProfile()
      fetchBlockStatus()
      reset()
    })
    watch([type, region], () => reset())
    watch(keyword, () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => reset(), 300)
    })

    async function toggleFollow() {
      if (!authStore.isLoggedIn) {
        showToast('请先登录')
        return
      }
      try {
        const r = await userStore.toggleFollow(userId.value)
        if (r.code === 0) {
          if (profile.value) {
            profile.value = { ...profile.value, isFollowing: !profile.value.isFollowing }
          }
          showSuccessToast(isFollowed.value ? '已关注' : '已取消关注')
        }
      } catch {
        showFailToast('操作失败')
      }
    }

    // 自己/他人 都可点击；他人通过 ?userId= 走 /api/v1/user/followers|followings 接口
    /** 粉丝 / 关注：本人看自己的，他人带 userId */
    const relationPath = (path: string) => (isSelf.value ? path : `${path}?userId=${userId.value}`)

    return () => (
      <div class="user-home">
        <VanNavBar title="用户主页" border={false}>
          {{
            left: () => (
              <VanIcon name="arrow-left" color="#fff" size="16" onClick={() => goBack()} />
            ),
            right: () =>
              isSelf.value ? null : (
                <VanIcon
                  name="ellipsis"
                  color="#fff"
                  size="16"
                  onClick={() => (menuShow.value = true)}
                />
              ),
          }}
        </VanNavBar>

        <div class="user-hd">
          <div class="user-home-info">
            <div class="user-home-img">
              <UserAvatar src={profile.value?.avatar} />
            </div>
            <div class="user-centent">
              <div class="name">
                {profile.value?.nickname || (isSelf.value ? authStore.user?.username : '')}
              </div>
              {!isSelf.value && (
                <div class="tool">
                  <div class="m-follow">
                    {/* 样式挂在 `.tool span` 上：span 保留，按钮放里面撑满 */}
                    <span class={isFollowed.value ? 'add-follow ok' : 'add-follow'}>
                      <button type="button" class="btn-block h-full" onClick={toggleFollow}>
                        {isFollowed.value ? '已关注' : '关注'}
                      </button>
                    </span>
                  </div>
                </div>
              )}
              <div class="medal-list"></div>
            </div>
          </div>
          <div class="hd-num">
            <RouterLink class="item btn-reset block" to={relationPath('/my-fans')}>
              <div class="icon-text">
                <div class="num">{profile.value?.fanCount ?? 0}</div>
                <div class="text">粉丝</div>
              </div>
            </RouterLink>
            <RouterLink class="item btn-reset block" to={relationPath('/my-follows')}>
              <div class="icon-text">
                <div class="num">{profile.value?.followCount ?? 0}</div>
                <div class="text">关注</div>
              </div>
            </RouterLink>
            <div class="item">
              <div class="icon-text">
                <div class="num">{profile.value?.publishCount ?? 0}</div>
                <div class="text">发布</div>
              </div>
            </div>
            <div class="item">
              <div class="icon-text">
                <div class="num">{profile.value?.userTotalSupport ?? 0}</div>
                <div class="text">获赞</div>
              </div>
            </div>
          </div>
        </div>

        <div class="tabs-bd">
          <div class="m-tabs">
            {CONTENT_TABS.map((tab) => (
              <button
                type="button"
                key={tab.key}
                class={['m-tabs-item btn-reset', type.value === tab.key ? 'act' : ''].join(' ')}
                onClick={() => (type.value = tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div class="ft-icon-text">
          <Tabs v-model:active={region.value} class="new-van-tabs" type="card" swipeThreshold={6}>
            {REGION_TABS.map((tab) => (
              <Tab key={tab.key} title={tab.label} name={tab.key} />
            ))}
          </Tabs>
        </div>

        <div class="search-cj">
          <VanSearch
            v-model={keyword.value}
            showAction
            placeholder={cplaceholder.value}
            onSearch={() => reset()}
          >
            {{
              action: () => (
                <button type="button" class="btn-reset" onClick={() => reset()}>
                  搜索
                </button>
              ),
            }}
          </VanSearch>
        </div>

        <div class="list-bd">
          <div role="feed" class="van-list">
            <ul class="forum-list">
              {items.value.map((article) => (
                <ForumListItem
                  key={article.id}
                  article={article}
                  withLabel={type.value !== 4}
                  to={
                    type.value === 4 ? `/articles/detail?id=${article.id}` : `/forum/detail/${article.id}`
                  }
                />
              ))}
            </ul>
          </div>
          {isEmpty.value && (
            <div class="text-center mt-[0.4rem] pb-[0.4rem]">
              <img src={emptyImg} class="w-[1.28rem]" alt="" />
              <p class="text-center text-[#a0a0a0] text-[0.32rem] leading-[0.44rem] mt-[0.28rem]">
                暂无数据
              </p>
            </div>
          )}
        </div>

        {/* 右上角菜单（分享 / 拉黑） */}
        <VanActionSheet
          v-model:show={menuShow.value}
          actions={menuActions.value}
          cancelText="取消"
          closeOnClickAction
          onSelect={onMenuSelect}
        />
        <SharePopup ref={shareRef} />
      </div>
    )
  },
})
